/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  BrickLoader,
  Pagination,
  UniversalWrap,
  CostTableData,
  StackBarChart,
  Wrapper,
  getCostAnalysisTableColumns,
  tooltipStyles,
  generateLinkParameters,
  mapStackBarChartColors,
  ChartType,
  CostAnalysisFields,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { useApplicationsCostPerServices } from "../hooks";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  showCostTable: boolean;
  showOtherServicesCost: boolean;
  handleOtherCostsSwitch: (enable: boolean) => void;
};

export function ApplicationsCostPerServicesView({
  startDate,
  endDate,
  granularity,
  period,
  showCostTable,
  showOtherServicesCost,
  handleOtherCostsSwitch,
}: Props) {
  const { currency } = useCurrency();

  const {
    costPerServicesChartData,
    costPerServicesTableData,
    costPerServicesTotal,
    loading,
    error,
    count,
    services,
    isCorrections,
  } = useApplicationsCostPerServices({
    startDate,
    endDate,
    granularity,
    showOtherServicesCost,
    handleOtherCostsSwitch,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const stackBarColors = useMemo(
    () => mapStackBarChartColors(services),
    [services]
  );

  const allColumns = useMemo(
    () =>
      getCostAnalysisTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerServicesTableData,
        period,
        chartType: ChartType.COST_PER_SERVICES,
        colors: stackBarColors,
        linkParameters: generateLinkParameters(
          LinkQueryParameterName.TYPE_SUBTYPE,
          []
        ),
      }),
    [
      startDate,
      endDate,
      currency,
      costPerServicesTableData,
      period,
      stackBarColors,
    ]
  );

  // hide total column when period is month
  const columns = useMemo(
    () =>
      period === Period.MONTH
        ? allColumns.filter((column) => column.header !== "Total")
        : allColumns,
    [allColumns, period]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <Wrapper onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <StackBarChart
                width={parent.width}
                height={280}
                data={costPerServicesChartData}
                granularity={granularity}
                loading={loading}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                showOthers={showOtherServicesCost}
                colors={stackBarColors}
                chartType={ChartType.COST_PER_SERVICES}
                containerRef={containerRef}
                keys={
                  isCorrections
                    ? [...services, CostAnalysisFields.CORRECTION_COST]
                    : services
                }
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set key to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </Wrapper>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerServicesTableData}
        chartType={ChartType.COST_PER_SERVICES}
        total={costPerServicesTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable && isNotEmpty(costPerServicesChartData)}
        hasFooter={false}
        hideHeader={period === Period.MONTH}
      />
      <Spacer height={theme.spacing.spacing03} />
      <Pagination small count={count ?? 0} />
    </UniversalWrap>
  );
}
